import { type ReactNode, lazy, Suspense } from 'react';
import { Route, Switch, Redirect } from 'wouter';
import { Loader2 } from 'lucide-react';
// Prerendered (SSG) marketing pages MUST stay eagerly imported: the client's
// first synchronous render has to match the puppeteer-captured HTML, otherwise
// hydration mismatches (blank flash / lost SEO). Everything else is lazy so a
// logged-out visitor doesn't download the whole authenticated app.
import { PrivacyPolicyPage } from '../../pages/PrivacyPolicy';
import { TermsPage } from '../../pages/Terms';
import { PromisePage } from '../../pages/PromisePage';
import { DownloadPage } from '../../pages/DownloadPage';
import { PocketLandingPage } from '../../pages/PocketLandingPage';
import { ExtensionLandingPage } from '../../pages/ExtensionLandingPage';
import { HomeLandingPage } from '../../pages/HomeLandingPage';
import { ImportLanding } from '../ImportLanding';
import { BROWSER_SOURCES } from '../../data/importSources';
// Light layout/gate wrappers stay eager — they're tiny shells reused across
// many (lazy) pages.
import { AdminLayout } from '../AdminLayout';
import { SettingsLayout } from '../SettingsLayout';
import { RouteGate } from '../RouteGate';

// ── Lazy (non-prerendered) routes ───────────────────────────────────────────
// Only fetched when their route is actually visited, so the entry chunk (which
// loads on every prerendered landing) stays small.
const AiOrganizeListPage = lazy(() =>
  import('../../pages/AiOrganizeListPage').then((m) => ({ default: m.AiOrganizeListPage })),
);
const AiOrganizeJobPage = lazy(() =>
  import('../../pages/AiOrganizeJobPage').then((m) => ({ default: m.AiOrganizeJobPage })),
);
const JobsPage = lazy(() => import('../../pages/JobsPage').then((m) => ({ default: m.JobsPage })));
const SettingsBalancePage = lazy(() =>
  import('../../pages/SettingsBalancePage').then((m) => ({ default: m.SettingsBalancePage })),
);
const SettingsApiKeysPage = lazy(() =>
  import('../../pages/SettingsApiKeysPage').then((m) => ({ default: m.SettingsApiKeysPage })),
);
const SettingsCategorizationPage = lazy(() =>
  import('../../pages/SettingsCategorizationPage').then((m) => ({
    default: m.SettingsCategorizationPage,
  })),
);
const SettingsDisplayPage = lazy(() =>
  import('../../pages/SettingsDisplayPage').then((m) => ({ default: m.SettingsDisplayPage })),
);
const SettingsResurfacePage = lazy(() =>
  import('../../pages/SettingsResurfacePage').then((m) => ({ default: m.SettingsResurfacePage })),
);
const SettingsBackupsPage = lazy(() =>
  import('../../pages/SettingsBackupsPage').then((m) => ({ default: m.SettingsBackupsPage })),
);
const SettingsGithubSyncPage = lazy(() =>
  import('../../pages/SettingsGithubSyncPage').then((m) => ({ default: m.SettingsGithubSyncPage })),
);
const SettingsAccountPage = lazy(() =>
  import('../../pages/SettingsAccountPage').then((m) => ({ default: m.SettingsAccountPage })),
);
const SettingsSpacesPage = lazy(() =>
  import('../../pages/SettingsSpacesPage').then((m) => ({ default: m.SettingsSpacesPage })),
);
const SettingsSecurityPage = lazy(() =>
  import('../../pages/SettingsSecurityPage').then((m) => ({ default: m.SettingsSecurityPage })),
);
const SpaceSettingsPage = lazy(() =>
  import('../../pages/SpaceSettingsPage').then((m) => ({ default: m.SpaceSettingsPage })),
);
const ExtensionAuthorizePage = lazy(() =>
  import('../../pages/ExtensionAuthorizePage').then((m) => ({ default: m.ExtensionAuthorizePage })),
);
const DiscoverPage = lazy(() =>
  import('../../pages/DiscoverPage').then((m) => ({ default: m.DiscoverPage })),
);
const DocsPage = lazy(() => import('../../pages/DocsPage').then((m) => ({ default: m.DocsPage })));
const ImportPage = lazy(() =>
  import('../../pages/ImportPage').then((m) => ({ default: m.ImportPage })),
);
const CleanupPage = lazy(() =>
  import('../../pages/CleanupPage').then((m) => ({ default: m.CleanupPage })),
);
const DuplicatesPage = lazy(() =>
  import('../../pages/DuplicatesPage').then((m) => ({ default: m.DuplicatesPage })),
);
const AskPage = lazy(() => import('../../pages/AskPage').then((m) => ({ default: m.AskPage })));
const FeatureDisabledPage = lazy(() =>
  import('../../pages/FeatureDisabledPage').then((m) => ({ default: m.FeatureDisabledPage })),
);
const LoginPage = lazy(() =>
  import('../../pages/LoginPage').then((m) => ({ default: m.LoginPage })),
);
const SignupPage = lazy(() =>
  import('../../pages/SignupPage').then((m) => ({ default: m.SignupPage })),
);
const PasswordResetRequestPage = lazy(() =>
  import('../../pages/PasswordResetRequestPage').then((m) => ({
    default: m.PasswordResetRequestPage,
  })),
);
const PasswordResetPage = lazy(() =>
  import('../../pages/PasswordResetPage').then((m) => ({ default: m.PasswordResetPage })),
);
const AuthExpiredPage = lazy(() =>
  import('../../pages/AuthExpiredPage').then((m) => ({ default: m.AuthExpiredPage })),
);

// Admin pages are gated behind login + role check and rarely visited; split
// them into their own chunks so the main bundle doesn't carry their weight.
const AdminPage = lazy(() => import('../../pages/AdminPage').then((m) => ({ default: m.AdminPage })));
const AdminProvidersPage = lazy(() =>
  import('../../pages/AdminProvidersPage').then((m) => ({ default: m.AdminProvidersPage })),
);
const AdminUsersPage = lazy(() =>
  import('../../pages/AdminUsersPage').then((m) => ({ default: m.AdminUsersPage })),
);
const AdminUserDetailPage = lazy(() =>
  import('../../pages/AdminUserDetailPage').then((m) => ({ default: m.AdminUserDetailPage })),
);
const AdminRateLimitsPage = lazy(() =>
  import('../../pages/AdminRateLimitsPage').then((m) => ({ default: m.AdminRateLimitsPage })),
);
const AdminRequestLimitsPage = lazy(() =>
  import('../../pages/AdminRequestLimitsPage').then((m) => ({ default: m.AdminRequestLimitsPage })),
);
const AdminAnomalyPage = lazy(() =>
  import('../../pages/AdminAnomalyPage').then((m) => ({ default: m.AdminAnomalyPage })),
);
const AdminAlertsPage = lazy(() =>
  import('../../pages/AdminAlertsPage').then((m) => ({ default: m.AdminAlertsPage })),
);
const AdminCatalogPage = lazy(() =>
  import('../../pages/AdminCatalogPage').then((m) => ({ default: m.AdminCatalogPage })),
);
const AdminAiQueuesPage = lazy(() =>
  import('../../pages/AdminAiQueuesPage').then((m) => ({ default: m.AdminAiQueuesPage })),
);
const AdminCategorizationPage = lazy(() =>
  import('../../pages/AdminCategorizationPage').then((m) => ({ default: m.AdminCategorizationPage })),
);
const AdminChatCostsPage = lazy(() =>
  import('../../pages/AdminChatCostsPage').then((m) => ({ default: m.AdminChatCostsPage })),
);
const AdminFeaturesPage = lazy(() =>
  import('../../pages/AdminFeaturesPage').then((m) => ({ default: m.AdminFeaturesPage })),
);
const AdminBackupsPage = lazy(() =>
  import('../../pages/AdminBackupsPage').then((m) => ({ default: m.AdminBackupsPage })),
);

// Shown while a lazy route chunk loads. Non-prerendered routes only, so a brief
// spinner here never affects SEO/hydration of the eager marketing pages.
function RouteFallback() {
  return (
    <div className="h-screen w-full flex items-center justify-center bg-zinc-50 dark:bg-zinc-950">
      <Loader2 className="w-8 h-8 animate-spin text-zinc-500" />
    </div>
  );
}

export function AppRoutes({ mainView }: { mainView: ReactNode }) {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Switch>
        <Route path="/privacy"><PrivacyPolicyPage /></Route>
        <Route path="/terms"><TermsPage /></Route>
        <Route path="/promise"><PromisePage /></Route>
        <Route path="/download"><DownloadPage /></Route>
        <Route path="/pocket"><PocketLandingPage /></Route>
        <Route path="/extension"><ExtensionLandingPage /></Route>
        {/* Landing is normally gated behind logged-out state via the catch-all
            mainView; expose it on an explicit path so logged-in users can reach
            it from the in-app logo without losing `/` as their app home. */}
        <Route path="/home"><HomeLandingPage /></Route>
        <Route path="/import"><ImportPage /></Route>
        <Route path="/import/pocket"><Redirect to="/import?variant=pocket" replace /></Route>
        {/* Per-source SEO import landings (`/import-from-<slug>`) — one literal
            route per source, driven by the importSources registry. A single
            `/import-from-:slug` route can't work: wouter treats `:slug` as a
            param only when it's a whole path segment, so `import-from-:slug`
            would match literally, not `import-from-chrome`. */}
        {BROWSER_SOURCES.map((source) => (
          <Route key={source.slug} path={`/import-from-${source.slug}`}>
            <ImportLanding source={source} />
          </Route>
        ))}
        <Route path="/cookies"><Redirect to="/privacy#cookies" replace /></Route>
        <Route path="/feature-disabled"><FeatureDisabledPage /></Route>
        <Route path="/ask">
          <RouteGate feature="chat_rag"><AskPage /></RouteGate>
        </Route>
        <Route path="/ai-organize">
          <RouteGate feature="ai_organize"><AiOrganizeListPage /></RouteGate>
        </Route>
        <Route path="/ai-organize/:jobId">
          {(params) => {
            const jobId =
              params && typeof params === 'object' ? (params as { jobId: string }).jobId : '';
            return (
              <RouteGate feature="ai_organize">
                <AiOrganizeJobPage jobId={jobId} />
              </RouteGate>
            );
          }}
        </Route>
        <Route path="/jobs"><JobsPage /></Route>
        <Route path="/settings"><Redirect to="/settings/balance" replace /></Route>
        <Route path="/settings/tokens"><Redirect to="/settings/balance" replace /></Route>
        <Route path="/settings/balance">
          <SettingsLayout><SettingsBalancePage /></SettingsLayout>
        </Route>
        <Route path="/settings/api-keys">
          <SettingsLayout><SettingsApiKeysPage /></SettingsLayout>
        </Route>
        <Route path="/settings/categorization">
          <SettingsLayout><SettingsCategorizationPage /></SettingsLayout>
        </Route>
        <Route path="/settings/display">
          <SettingsLayout><SettingsDisplayPage /></SettingsLayout>
        </Route>
        <Route path="/settings/resurface">
          <SettingsLayout><SettingsResurfacePage /></SettingsLayout>
        </Route>
        <Route path="/settings/backups">
          <SettingsLayout>
            <RouteGate feature="periodic_backup_email"><SettingsBackupsPage /></RouteGate>
          </SettingsLayout>
        </Route>
        <Route path="/settings/github-sync">
          <SettingsLayout>
            <RouteGate feature="github_sync"><SettingsGithubSyncPage /></RouteGate>
          </SettingsLayout>
        </Route>
        <Route path="/settings/spaces">
          <SettingsLayout><SettingsSpacesPage /></SettingsLayout>
        </Route>
        <Route path="/settings/spaces/:slug">
          <SettingsLayout><SpaceSettingsPage /></SettingsLayout>
        </Route>
        <Route path="/settings/account">
          <SettingsLayout><SettingsAccountPage /></SettingsLayout>
        </Route>
        <Route path="/settings/security">
          <SettingsLayout><SettingsSecurityPage /></SettingsLayout>
        </Route>
        <Route path="/extension/authorize"><ExtensionAuthorizePage /></Route>
        <Route path="/admin">
          <AdminPage />
        </Route>
        <Route path="/admin/providers">
          <AdminLayout><AdminProvidersPage /></AdminLayout>
        </Route>
        <Route path="/admin/users">
          <AdminLayout><AdminUsersPage /></AdminLayout>
        </Route>
        <Route path="/admin/users/:id">
          {(params) => {
            const id =
              params && typeof params === 'object' ? (params as { id: string }).id : '';
            return (
              <AdminLayout><AdminUserDetailPage userId={id} /></AdminLayout>
            );
          }}
        </Route>
        <Route path="/admin/rate-limits">
          <AdminLayout><AdminRateLimitsPage /></AdminLayout>
        </Route>
        <Route path="/admin/request-limits">
          <AdminLayout><AdminRequestLimitsPage /></AdminLayout>
        </Route>
        <Route path="/admin/anomaly">
          <AdminLayout><AdminAnomalyPage /></AdminLayout>
        </Route>
        <Route path="/admin/alerts">
          <AdminLayout><AdminAlertsPage /></AdminLayout>
        </Route>
        <Route path="/admin/catalog">
          <AdminLayout><AdminCatalogPage /></AdminLayout>
        </Route>
        <Route path="/admin/ai-queues">
          <AdminLayout><AdminAiQueuesPage /></AdminLayout>
        </Route>
        <Route path="/admin/categorization">
          <AdminLayout>
            <RouteGate feature="categorization"><AdminCategorizationPage /></RouteGate>
          </AdminLayout>
        </Route>
        <Route path="/admin/chat-costs">
          <AdminLayout><AdminChatCostsPage /></AdminLayout>
        </Route>
        <Route path="/admin/features">
          <AdminLayout><AdminFeaturesPage /></AdminLayout>
        </Route>
        <Route path="/admin/backups">
          <AdminLayout><AdminBackupsPage /></AdminLayout>
        </Route>
        <Route path="/discover">
          <RouteGate feature="discover"><DiscoverPage /></RouteGate>
        </Route>
        <Route path="/space/:slug/settings">
          {(params) => {
            const slug =
              params && typeof params === 'object' ? (params as { slug: string }).slug : '';
            return <Redirect to={`/settings/spaces/${slug}`} replace />;
          }}
        </Route>
        <Route path="/docs"><DocsPage /></Route>
        <Route path="/cleanup"><CleanupPage /></Route>
        <Route path="/duplicates"><DuplicatesPage /></Route>
        <Route path="/login"><LoginPage /></Route>
        <Route path="/signup"><SignupPage /></Route>
        <Route path="/auth/reset"><PasswordResetRequestPage /></Route>
        <Route path="/auth/reset/:token">
          {(params) => <PasswordResetPage token={(params as { token: string }).token} />}
        </Route>
        <Route path="/auth/expired"><AuthExpiredPage /></Route>
        <Route>{mainView}</Route>
      </Switch>
    </Suspense>
  );
}
